import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Flame, FolderKanban, GraduationCap, CheckCircle2, ArrowRight, AlertTriangle } from "lucide-react";

/**
 * PDL-Cockpit · "Was brennt heute?"
 * Zeigt beim Login sofort: rote/orange Mappen + offene & überfällige Fortbildungen.
 * Nur sichtbar für User mit Pflege-KI-Zugang (Backend liefert sonst 403 → Widget bleibt weg).
 */
export const PflegeCockpit = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/pflege-cockpit`, { withCredentials: true })
      .then(({ data }) => { if (active) setData(data); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  if (!data) return null;

  const { mappen, fortbildungen } = data;
  const brennend = mappen.brennend || [];
  const ueberfaellig = fortbildungen.ueberfaellig || [];
  const topOffen = fortbildungen.top_offen || [];
  const offenCount = (fortbildungen.counts?.offen || 0) + (fortbildungen.counts?.geplant || 0);
  const allesRuhig = brennend.length === 0 && ueberfaellig.length === 0 && topOffen.length === 0;

  return (
    <section
      data-testid="pflege-cockpit"
      className="rounded-3xl border border-cream-300 bg-white overflow-hidden"
    >
      <div className="px-6 sm:px-7 pt-5 pb-4 flex items-center justify-between gap-3 border-b border-cream-300">
        <div className="flex items-center gap-2.5">
          <span className={`inline-flex items-center justify-center h-8 w-8 rounded-full ${allesRuhig ? "bg-emerald-100 text-emerald-700" : "bg-brand-darkred/10 text-brand-darkred"}`}>
            {allesRuhig ? <CheckCircle2 className="h-4 w-4" strokeWidth={2} /> : <Flame className="h-4 w-4" strokeWidth={2} />}
          </span>
          <div>
            <h2 className="font-display text-sand-800 text-[1.15rem] font-semibold leading-tight">
              {allesRuhig ? "Alles ruhig heute" : "Was heute brennt"}
            </h2>
            <p className="text-[12px] text-sand-500">
              {mappen.total} Mappen · {mappen.counts.rot} rot · {mappen.counts.orange} orange · {offenCount} Fortbildungen offen
            </p>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-1.5">
          <AmpelDot color="bg-red-500" n={mappen.counts.rot} />
          <AmpelDot color="bg-orange-400" n={mappen.counts.orange} />
          <AmpelDot color="bg-yellow-400" n={mappen.counts.gelb} />
          <AmpelDot color="bg-emerald-500" n={mappen.counts.gruen} />
        </div>
      </div>

      {allesRuhig ? (
        <div className="px-6 sm:px-7 py-5 text-[13.5px] text-sand-600" data-testid="cockpit-ruhig">
          Keine roten Mappen, keine überfälligen Fortbildungen. Guter Start in den Tag.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-cream-300">
          {/* Rote / orange Mappen */}
          <div className="px-6 sm:px-7 py-5">
            <div className="flex items-center justify-between mb-3">
              <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-bold text-brand-darkred">
                <FolderKanban className="h-3.5 w-3.5" strokeWidth={2} /> Mappen mit Risiko
              </span>
              <Link to="/campus/app/patientenmappe" className="text-[12px] text-sand-500 hover:text-terra underline-offset-4 hover:underline">
                Alle →
              </Link>
            </div>
            {brennend.length === 0 ? (
              <p className="text-[13px] text-sand-500">Keine roten oder orangen Mappen. 👌</p>
            ) : (
              <ul className="space-y-2" data-testid="cockpit-mappen-list">
                {brennend.map((m) => (
                  <li key={m.id}>
                    <Link
                      to={`/campus/app/patientenmappe/${m.id}`}
                      data-testid={`cockpit-mappe-${m.id}`}
                      className="group flex items-center gap-3 rounded-xl border border-cream-300 hover:border-terra/50 px-3.5 py-2.5 transition-colors"
                    >
                      <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${m.risk_level === "rot" ? "bg-red-500" : "bg-orange-400"}`} />
                      <div className="min-w-0 flex-1">
                        <div className="text-[13.5px] font-semibold text-sand-800 truncate">{m.label}</div>
                        {m.risk_topics?.length > 0 && (
                          <div className="text-[11.5px] text-sand-500 truncate">
                            {m.risk_topics.slice(0, 4).join(" · ")}
                          </div>
                        )}
                      </div>
                      <ArrowRight className="h-3.5 w-3.5 text-sand-400 group-hover:text-terra group-hover:translate-x-0.5 transition-all shrink-0" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Fortbildungen */}
          <div className="px-6 sm:px-7 py-5">
            <div className="flex items-center justify-between mb-3">
              <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-bold text-terra">
                <GraduationCap className="h-3.5 w-3.5" strokeWidth={2} /> Fortbildungen
              </span>
              <Link to="/campus/app/fortbildungsplan" className="text-[12px] text-sand-500 hover:text-terra underline-offset-4 hover:underline">
                Zum Plan →
              </Link>
            </div>
            {ueberfaellig.length === 0 && topOffen.length === 0 ? (
              <p className="text-[13px] text-sand-500">Nichts offen. 👌</p>
            ) : (
              <ul className="space-y-2" data-testid="cockpit-fortbildungen-list">
                {ueberfaellig.map((f) => (
                  <FortbildungRow key={f.id} f={f} overdue />
                ))}
                {topOffen.slice(0, Math.max(0, 4 - ueberfaellig.length)).map((f) => (
                  <FortbildungRow key={f.id} f={f} />
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

function FortbildungRow({ f, overdue }) {
  return (
    <li>
      <Link
        to="/campus/app/fortbildungsplan"
        data-testid={`cockpit-fortbildung-${f.id}`}
        className={`group flex items-center gap-3 rounded-xl border px-3.5 py-2.5 transition-colors ${
          overdue ? "border-brand-darkred/30 bg-brand-darkred/[0.04] hover:border-brand-darkred/60" : "border-cream-300 hover:border-terra/50"
        }`}
      >
        {overdue ? (
          <AlertTriangle className="h-3.5 w-3.5 text-brand-darkred shrink-0" strokeWidth={2} />
        ) : (
          <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${f.prioritaet === "hoch" ? "bg-red-500" : f.prioritaet === "mittel" ? "bg-yellow-400" : "bg-emerald-500"}`} />
        )}
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-semibold text-sand-800 truncate">{f.titel}</div>
          <div className="text-[11.5px] text-sand-500 truncate">
            {overdue && f.termin ? `überfällig seit ${new Date(f.termin).toLocaleDateString("de-DE")}` : f.mappe_label || f.status}
          </div>
        </div>
        <ArrowRight className="h-3.5 w-3.5 text-sand-400 group-hover:text-terra group-hover:translate-x-0.5 transition-all shrink-0" />
      </Link>
    </li>
  );
}

function AmpelDot({ color, n }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-cream-300 px-2 py-0.5 text-[11px] font-semibold text-sand-600">
      <span className={`h-2 w-2 rounded-full ${color}`} /> {n}
    </span>
  );
}
